import { FC, useState, useRef, useEffect } from 'react'
import { SectionMasonryGalleryProps } from '@portfolio/models'
import { Media, SectionWrapper } from '@portfolio/components'
import { styled } from '@portfolio/styles'

const GalleryWrapper = styled(SectionWrapper, {
  position: 'relative',
})

const GalleryContainer = styled('div', {
  display: 'flex',
  gap: 0,
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollSnapType: 'x mandatory',
  scrollPadding: 0,

  '&::-webkit-scrollbar': {
    display: 'none',
  },

  '@lg': {
    display: 'block',
    columnCount: 3,
    columnGap: '$20',
    overflowX: 'visible',
    padding: '0 $30',
  },
})

const MediaWrapper = styled('div', {
  position: 'relative',
  aspectRatio: '0.8',
  minWidth: '100vw',
  scrollSnapAlign: 'start',
  overflow: 'hidden',

  '@lg': {
    aspectRatio: '1.33',
    minWidth: 'auto',
    borderRadius: '$8',
    marginBottom: '$20',
    breakInside: 'avoid',
    display: 'inline-block',
    width: '100%',
  },

  variants: {
    tallDesktop: {
      true: {
        '@lg': {
          aspectRatio: '0.8',
        },
      },
    },
  },
})

const Gradient = styled('div', {
  position: 'absolute',
  insetInline: 0,
  bottom: 0,
  left: 0,
  height: '160px',
  background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, $black_06 100%)',
  pointerEvents: 'none',
  zIndex: '$content',

  '@lg': {
    display: 'none',
  },
})

const IndicatorsContainer = styled('div', {
  position: 'absolute',
  bottom: 'calc($20 + $24)', // $20 for the spacing, $24 for the container padding
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '$24',
  zIndex: '$videoControls',

  '@lg': {
    display: 'none',
  },
})

const Indicator = styled('button', {
  width: '$12',
  height: '$12',
  borderRadius: '50%',
  backgroundColor: '$gray300',
  transition: 'background-color 0.3s ease',
  border: 'none',
  padding: 0,
  cursor: 'pointer',

  '&:focus-visible': {
    outline: '$focusBlue solid 3px;',
  },

  variants: {
    active: {
      true: {
        backgroundColor: '$white',
      },
    },
  },
})

export const SectionMasonryGallery: FC<SectionMasonryGalleryProps> = ({
  gallery,
  spacing,
  topBorder,
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft
      const itemWidth = container.clientWidth
      const newIndex = Math.round(scrollLeft / itemWidth)
      setActiveIndex(newIndex)
    }

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToIndex = (index: number) => {
    const container = scrollContainerRef.current
    if (!container) return

    const itemWidth = container.clientWidth
    const scrollPosition = index * itemWidth

    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth',
    })
  }

  const handleIndicatorClick = (index: number) => {
    setActiveIndex(index)
    scrollToIndex(index)
  }

  if (!gallery || gallery.length === 0) {
    return null
  }

  return (
    <GalleryWrapper type={spacing || 'MEDIUM'} topBorder={topBorder}>
      <GalleryContainer ref={scrollContainerRef}>
        {gallery.map((mediaItem, index) => {
          // 1st, 5th, 8th images (indices 0, 4, 7) have aspect ratio 0.8 on desktop
          const isTallDesktop = index === 0 || index === 4 || index === 7

          return (
            <MediaWrapper key={index} tallDesktop={isTallDesktop}>
              {mediaItem?.mediaPayload && (
                <>
                  <Media
                    {...mediaItem}
                    sizes="(min-width: 1000px) 33vw, 100vw"
                    layout="fill"
                    objectFit="cover"
                  />
                  <Gradient />
                </>
              )}
            </MediaWrapper>
          )
        })}
      </GalleryContainer>
      <IndicatorsContainer>
        {gallery.map((_, index) => (
          <Indicator
            key={index}
            active={index === activeIndex}
            onClick={() => handleIndicatorClick(index)}
          />
        ))}
      </IndicatorsContainer>
    </GalleryWrapper>
  )
}
